import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  FormControlDirective,
  FormLabelDirective,
  MultiSelectComponent,
  MultiSelectOptionComponent,
  TabsComponent,
  TabsListComponent,
  TabsContentComponent,
  TabPanelComponent,
  TabDirective
} from '@coreui/angular-pro';
import { TranslatePipe, I18nService } from 'i18n';
import { AuthService } from 'auth';
import { BootstrapService } from 'app';
import { HttpClient } from '@angular/common/http';
import { FoundingMemberBadgeComponent, Logger } from 'shared';
import { CommunityProfileSectionComponent } from 'community';
import { take, startWith } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

interface ProfileData {
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: string;
  address2?: string;
  city?: string;
  postcode?: string;
  state_code?: string;
  country_code?: string;
  mobile?: string;
  dob?: string;
  gender?: string;
  language?: string;
}

interface Country {
  code: string;
  name: string;
  states?: { code: string; name: string }[];
}

interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-profile-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    FormControlDirective,
    FormLabelDirective,
    ButtonDirective,
    MultiSelectComponent,
    MultiSelectOptionComponent,
    TabsComponent,
    TabsListComponent,
    TabsContentComponent,
    TabPanelComponent,
    TabDirective,
    TranslatePipe,
    FoundingMemberBadgeComponent,
    CommunityProfileSectionComponent
  ],
  templateUrl: './profile-page.component.html',
  styleUrls: ['./profile-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfilePageComponent {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private bootstrapService = inject(BootstrapService);
  private i18nService = inject(I18nService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  protected logger = new Logger('ProfilePageComponent');

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  activeTab = signal<number>(0);
  loading = signal<boolean>(false);
  message = signal<{ type: 'success' | 'error'; text: string } | null>(null);
  emailVerified = signal<boolean>(true);
  resendingEmail = signal<boolean>(false);

  // "Founding Member" — one of the first 1.000 paying WolfPack Health Club
  // members. Read straight off the auth payload; absent on brands/backends that
  // do not have the feature, in which case the badge simply never renders.
  foundingMember = signal<boolean>(this.authService.getUser()?.founding_member === true);
  foundingMemberNumber = signal<number | null>(this.authService.getUser()?.founding_member_number ?? null);

  // "Founding Practitioner" — the same honour for the first 1.000 Practitioner
  // Network members. Read the same way, and rendered next to (not instead of) the
  // Founding Member badge: the two are separate distinctions.
  foundingPractitioner = signal<boolean>(this.authService.getUser()?.founding_practitioner === true);
  foundingPractitionerNumber = signal<number | null>(this.authService.getUser()?.founding_practitioner_number ?? null);

  // Community Network profile tab: offered unless the backend explicitly
  // turned the feature off for this user (`flag:community_enabled`), same
  // rule as the menu entry and the community route guard.
  communityEnabled = signal<boolean>(this.authService.getUser()?.community_enabled !== false);

  countriesData = signal<Country[]>([]);
  countries = signal<SelectOption[]>([]);
  filteredCountries = signal<SelectOption[]>([]);

  originalEmail = signal<string>('');

  // Forms
  profileForm = this.fb.group({
    first_name: [''],
    last_name: [''],
    email: ['', [Validators.required, Validators.email]],
    mobile: [''],
    dob: [''],
    gender: [''],
    address: [''],
    address2: [''],
    city: [''],
    postcode: [''],
    country_code: [''],
    state_code: [''],
    language: ['en']
  });

  /** Tracks country selection so state/province computed() updates (form values are not signals). */
  private readonly countryCode = toSignal(
    this.profileForm.get('country_code')!.valueChanges.pipe(
      startWith(this.profileForm.get('country_code')!.value ?? '')
    )
  );

  passwordForm = this.fb.group({
    current_password: ['', Validators.required],
    password: ['', [Validators.required, Validators.minLength(8)]],
    password_confirmation: ['', Validators.required]
  }, { validators: this.passwordMatchValidator });

  // Computed helpers
  selectedCountryStates = computed<SelectOption[]>(() => {
    const code = this.normalizeSelectValue(this.countryCode());
    if (!code) return [];
    const country = this.countriesData().find(c => c.code === code);
    return country?.states?.map(s => ({ value: s.code, label: s.name })) || [];
  });

  hasStates = computed(() => this.selectedCountryStates().length > 0);

  genders = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' }
  ];

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Legacy backends may omit the field; only an explicit false means unverified.
    effect(() => {
      const user = this.authService.getUser();
      this.emailVerified.set(user?.email_confirmed !== false);
    });

    this.profileForm
      .get('country_code')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.profileForm.patchValue({ state_code: '' }, { emitEvent: false });
      });

    // Load initial data
    this.loadCountries();
    this.loadProfile();
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private loadCountries(): void {
    this.http.get<Country[]>('/countries')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.countriesData.set(data);
          this.countries.set(data.map(c => ({ value: c.code, label: c.name })));
          this.filteredCountries.set(this.countries());
        },
        error: err => this.logger.error('Failed to load countries', err)
      });
  }

  filterCountries(searchTerm: string): void {
    if (!searchTerm.trim()) {
      this.filteredCountries.set(this.countries());
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = this.countries().filter(c =>
      c.label.toLowerCase().includes(term) || c.value.toLowerCase().includes(term)
    );
    this.filteredCountries.set(filtered);
  }

  private loadProfile(): void {
    this.loading.set(true);

    this.http.get<ProfileData>('/profile')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.originalEmail.set(data.email || '');
          this.profileForm.patchValue({
            first_name: data.first_name || '',
            last_name: data.last_name || '',
            email: data.email || '',
            mobile: data.mobile || '',
            dob: data.dob || '',
            gender: data.gender || '',
            address: data.address || '',
            address2: data.address2 || '',
            city: data.city || '',
            postcode: data.postcode || '',
            country_code: data.country_code || '',
            state_code: data.state_code || '',
            language: data.language || 'en'
          });
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load profile', err);
          this.showMessage('error', 'Failed to load profile data');
          this.loading.set(false);
        }
      });
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      this.showMessage('error', 'Please fill in all required fields correctly');
      return;
    }

    this.loading.set(true);
    const formData = this.profileForm.value;

    // Email changed → check availability first
    if (formData.email && formData.email !== this.originalEmail()) {
      this.http.get(`/auth/check_email?email=${encodeURIComponent(formData.email)}`)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            if (res.exists) {
              this.showMessage('error', 'This email is already in use by another user.');
              this.loading.set(false);
            } else {
              this.updateProfile(formData);
            }
          },
          error: err => {
            this.logger.error('Failed to check email', err);
            this.showMessage('error', 'Failed to verify email availability');
            this.loading.set(false);
          }
        });
    } else {
      this.updateProfile(formData);
    }
  }

  private updateProfile(formData: any): void {
    this.http.put<ProfileData>('/profile', { profile: formData })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.bootstrapService.bootstrap().pipe(take(1))
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(() => {
                this.loading.set(false);
                const msg = formData.email !== this.originalEmail()
                  ? 'Profile updated. Email change requires confirmation.'
                  : 'Profile updated successfully';
                this.showMessage('success', msg);
                this.originalEmail.set(formData.email);

                const url = `/${this.i18nService.getLanguage()}/profile`;
                if (this.emailVerified() && this.router.url !== url) {
                  this.router.navigateByUrl(url);
                }
              });
            },
            error: err => {
              this.logger.error('Failed to update profile', err);
              const msg = err.error?.errors?.join(', ') || 'Failed to update profile';
              this.showMessage('error', msg);
              this.loading.set(false);
            }
          });
  }

  changePassword(): void {
    if (this.passwordForm.invalid) return;

    this.loading.set(true);

    this.http.put('/profile/password', { profile: this.passwordForm.value })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.showMessage('success', 'Password changed successfully');
          this.passwordForm.reset();
          this.loading.set(false);
        },
        error: err => {
          this.logger.error('Failed to change password', err);
          const msg = err.error?.errors?.join(', ') || 'Failed to change password';
          this.showMessage('error', msg);
          this.loading.set(false);
        }
      });
  }

  private passwordMatchValidator(formGroup: FormGroup) {
    const password = formGroup.get('password');
    const confirm = formGroup.get('password_confirmation');

    if (!password || !confirm) return null;

    if (confirm.errors && !confirm.errors['passwordMismatch']) return null;

    if (password.value !== confirm.value) {
      confirm.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }

    confirm.setErrors(null);
    return null;
  }

  private showMessage(type: 'success' | 'error', text: string): void {
    this.message.set({ type, text });
    setTimeout(() => this.message.set(null), 5000);
  }

  /** CoreUI multi-select may emit a scalar or a one-element array for single mode. */
  private normalizeSelectValue(value: unknown): string {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) return String(value[0] ?? '');
    return String(value);
  }

  resendVerificationEmail(): void {
    const user = this.authService.getUser();
    if (!user?.email) {
      this.showMessage('error', 'No email address found');
      return;
    }

    this.resendingEmail.set(true);

    this.http.post('/auth/resend_confirmation', { email: user.email })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: any) => {
            this.showMessage('success', res.message || 'Verification email sent. Check your inbox.');
            this.resendingEmail.set(false);
          },
          error: err => {
            this.logger.error('Failed to resend verification email', err);
            const msg = err.error?.error || 'Failed to send verification email';
            this.showMessage('error', msg);
            this.resendingEmail.set(false);
          }
        });
  }

  navigateToDeleteAccount(): void {
    const lang = this.i18nService.getLanguage();
    this.router.navigate(['/', lang, 'delete-account']);
  }
}